import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { useGetAgentSessionBomItemsQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type IAgentSessionBomLineItem from 'store/slices/valueChainMap/interfaces/IAgentSessionBomLineItem';
import type IUseAICreatorBoMTableParams from '../interfaces/IUseAICreatorBoMTableParams';

const useAICreatorBoMTable = ({
  valueChainMapId,
  sessionId,
  currentBomId,
  listContextKey,
  open,
}: IUseAICreatorBoMTableParams) => {
  const [offset, setOffset] = useState(0);
  const [tableItems, setTableItems] = useState<IAgentSessionBomLineItem[]>([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const lastAppliedDataKeyRef = useRef<string>('');

  const {
    currentData: agentSessionBomItems,
    isFetching: isAgentSessionBomItemsFetching,
    refetch: refetchAgentSessionBomItems,
    fulfilledTimeStamp,
  } = useGetAgentSessionBomItemsQuery({
    valueChainMapId: valueChainMapId || '',
    sessionId: sessionId || 0,
    bomId: currentBomId || 0,
    offset,
    limit: DATA_SET_PAGINATION_COUNT,
  }, {
    skip: !sessionId || !valueChainMapId || !currentBomId,
  });

  const canRefetchAgentSessionBomItems = Boolean(sessionId && valueChainMapId && currentBomId);

  const { meta, items } = agentSessionBomItems?.data || {};
  const hasMore = meta?.hasNext || false;

  const onLoadMore = useCallback(() => {
    if (hasMore) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  }, [hasMore]);

  const handleRefreshAgentSessionBomItems = useCallback(() => {
    if (!canRefetchAgentSessionBomItems) {
      return;
    }

    setRefreshToken((prev) => prev + 1);

    if (offset === 0) {
      refetchAgentSessionBomItems();
      return;
    }

    setOffset(0);
  }, [canRefetchAgentSessionBomItems, offset, refetchAgentSessionBomItems]);

  useEffect(() => {
    setOffset(0);
    setTableItems([]);
    setRefreshToken(0);
    lastAppliedDataKeyRef.current = '';
  }, [listContextKey, open, sessionId]);

  useEffect(() => {
    if (!open || !currentBomId || !items) {
      return;
    }

    const dataKey = [
      sessionId || 0,
      listContextKey,
      currentBomId,
      offset,
      refreshToken,
      fulfilledTimeStamp || 0,
    ].join('-');

    if (lastAppliedDataKeyRef.current === dataKey) {
      return;
    }

    if (offset === 0) {
      setTableItems(items);
      lastAppliedDataKeyRef.current = dataKey;
      return;
    }

    setTableItems((prev) => {
      const existingIds = new Set(prev.map((item) => item.id));
      const newItems = items.filter((item) => !existingIds.has(item.id));

      return [...prev, ...newItems];
    });
    lastAppliedDataKeyRef.current = dataKey;
  }, [currentBomId, fulfilledTimeStamp, items, listContextKey, offset, open, refreshToken, sessionId]);

  return {
    tableItems,
    setTableItems,
    onLoadMore,
    hasMore,
    isAgentSessionBomItemsFetching,
    refetchAgentSessionBomItems: handleRefreshAgentSessionBomItems,
  };
};

export default useAICreatorBoMTable;
